<template>
  <div class="search-results">
    <div class="teaser-grid is-TeaserThird">
      <div class="teaser-grid-inner">
        <div
          v-for="item in teasers"
          :key="item.nid"
          class="search-results-item"
        >
          <TeaserThird
            :title="item.title"
            :media="item.image"
            :tagline="item.tagline"
            :url="item.url"
            :show-opening-hours="showOpeningHoursStatus"
          />
          <div v-if="hasMap" class="search-results-item-actions">
            <button class="button is-small" @click="showOnMap(item.nid)">
              <SpriteSymbol name="map" />
              <span class="belowTablet:hidden">{{
                $texts('showOnMap')
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ApiSearchResponseItem } from '~~/layers/legacy-search/app/types'
import { formatDistance } from '~/helpers'

const props = withDefaults(
  defineProps<{
    items: ApiSearchResponseItem[]
    hasMap?: boolean
    showOpeningHoursStatus?: boolean
  }>(),
  {
    items: () => [],
    hasMap: false,
    showOpeningHoursStatus: false,
  },
)

const route = useRoute()
const router = useRouter()
const { $texts } = useEasyTexts()

const hasLocationFilter = computed(() => !!route.query.location)

const teasers = computed(() =>
  props.items.map((v) => {
    const tagline = hasLocationFilter.value ? formatDistance(v.distance) : ''
    return {
      ...v,
      tagline,
    }
  }),
)

function showOnMap(nid?: string) {
  if (!nid) {
    return
  }
  router.push({
    path: route.path,
    query: {
      ...route.query,
      center: undefined,
      id: nid,
      map: 'true',
      zoom: '18',
    },
  })
}
</script>

<style>
@reference '~/assets/css/tailwind.css';
.search-results-item {
  @apply relative;

  .teaser {
    .opening-hours-status {
      margin-top: 0.5rem;
    }
    .link {
      @apply hidden;
    }
  }
}

.search-results-item-actions {
  @apply absolute top-0 right-0 p-0;

  .button {
    @apply items-center justify-center p-0 text-center;
    background: none;
    border: none;
    font-size: 12px;
    height: 2.5rem;
    width: 2.5rem;
    min-width: auto;

    .icon {
      align-items: center;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 100%;
      color: #fff;
      display: flex;
      height: 1.5rem;
      justify-content: center;
      margin: 0 !important;
      padding: 3px 0 0;
      width: 1.5rem;
    }
  }

  .search-results.is-loading & {
    display: none !important;
  }

  @apply tablet:p-0;
  @variant tablet {
    padding: 0.5em;
    display: none;
  }

  .search-results-item:hover & {
    @apply tablet:block;
  }
  .button {
    @apply tablet:border-2 tablet:border-solid tablet:border-border tablet:bg-scheme-main;
    @variant tablet {
      font-size: 12px;
      height: auto;
      width: auto;
      min-width: auto;
      height: 3em;
      line-height: 1.5;
      padding-bottom: calc(0.5em - 1px);
      padding-left: calc(0.75em - 1px);
      padding-right: calc(0.75em - 1px);
      padding-top: calc(0.5em - 1px);
      vertical-align: top;
    }

    &:hover,
    &.is-hovered {
      @apply tablet:border-grey-light tablet:bg-scheme-main-bis tablet:text-scheme-invert;
    }

    &:focus,
    &.is-focused,
    &:active,
    &.is-active {
      @apply tablet:border-grey-dark tablet:text-grey-darker;
    }

    .icon {
      @apply tablet:!m-0 tablet:flex tablet:items-center tablet:justify-center tablet:rounded-full tablet:p-0 tablet:pr-[10px] tablet:text-white;

      @variant tablet {
        margin: 0 !important;
        background: none;
        width: 1.5rem;
        height: 1.5rem;
        padding-top: 3px;
      }

      & .mdi-map::before {
        @apply tablet:text-button-color;
      }
    }
  }
}
</style>
